import { useState, useEffect } from 'react';
import { pwaManager } from '@/utils/PwaManager';
import { isIOS, isStandalone } from '@/utils/PWAUtils';

export const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    const handlePromptUpdate = (prompt) => {
      setInstallPrompt(prompt);
      updateCanInstall(prompt);
    };

    pwaManager.subscribe(handlePromptUpdate);

    // Initial check
    const currentPrompt = pwaManager.getInstallPrompt();
    setInstallPrompt(currentPrompt);
    updateCanInstall(currentPrompt);

    return () => {
      pwaManager.unsubscribe(handlePromptUpdate);
    };
  }, []);

  const updateCanInstall = (prompt) => {
    if (isStandalone()) {
      setCanInstall(false);
      return;
    }

    if (isIOS() || prompt || pwaManager.isPromptAvailable()) {
      setCanInstall(true);
    } else {
      setCanInstall(false);
    }
  };

  const installApp = async (onIosInstall) => {
    if (isInstalling) return { success: false, message: 'Installation in progress' };

    setIsInstalling(true);

    try {
      if (isIOS()) {
        onIosInstall?.();
        return { success: true, message: 'iOS install instructions shown' };
      }

      const result = await pwaManager.installApp();
      return result;
    } finally {
      setIsInstalling(false);
    }
  };

  const showFallbackInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    let instructions = '';

    if (userAgent.includes('chrome')) {
      instructions =
        'Click the three dots menu → "Install app" or look for the install icon in the address bar.';
    } else if (userAgent.includes('firefox')) {
      instructions = 'Click the three lines menu → "Install this site as an app".';
    } else if (userAgent.includes('safari')) {
      instructions = 'Click the share button → "Add to Home Screen".';
    } else if (userAgent.includes('edge')) {
      instructions = 'Click the three dots menu → "Apps" → "Install this site as an app".';
    } else {
      instructions = 'Look for an "Install app" option in your browser menu or address bar.';
    }

    return instructions;
  };

  return {
    installPrompt,
    canInstall,
    isInstalling,
    installApp,
    showFallbackInstructions,
  };
};
